import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import http from "../../services/http";
import { useNotification } from "../../context/NotificationContext";
import { CircularProgress, Box } from "@mui/material";

export default function PrinterConfig() {
  const { showNotification } = useNotification();
  const showNotificationRef = useRef(showNotification);
  const navigate = useNavigate();
  const sessionExpiredRef = useRef(false);

  const [currentConfig, setCurrentConfig] = useState(null);
  const [config, setConfig] = useState({
    printerName: "",
    printerIp: "",
    port: "",
    mode: "USB", // Default to USB
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const MODE_OPTIONS = ["USB", "WiFi", "LAN"];

  const resetForm = useCallback(() => {
    setConfig({
      printerName: "",
      printerIp: "",
      port: "",
      mode: "USB", // Default to USB
    });
  }, []);

  const fetchConfig = useCallback(async () => {
    if (sessionExpiredRef.current) return;
    setLoading(true);
    try {
      const res = await http.get("/printerconfig");

      if (res?.data?.success && res.data.data) {
        const configs = Array.isArray(res.data.data) ? res.data.data : [];
        // Get the first config (only one record allowed)
        if (configs.length > 0) {
          const firstConfig = configs[0];
          setCurrentConfig(firstConfig);
          setConfig({
            printerName: firstConfig.printerName || "",
            printerIp: firstConfig.printerIp || "",
            port: firstConfig.port || "",
            mode: firstConfig.mode || "USB",
          });
        } else {
          setCurrentConfig(null);
          resetForm();
        }
      } else {
        setCurrentConfig(null);
        resetForm();
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        showNotificationRef.current(
          "Session expired. Please login again.",
          "error"
        );
        localStorage.removeItem("token");
        sessionExpiredRef.current = true;
        navigate("/login", { replace: true });
        return;
      }
      console.error("Error fetching printer config:", err);
      showNotificationRef.current(
        err.response?.data?.message || "Failed to load printer configuration",
        "error"
      );
      setCurrentConfig(null);
      resetForm();
    } finally {
      setLoading(false);
    }
  }, [navigate, resetForm]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleChange = (e) => {
    setConfig({ ...config, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Validation
    if (
      !config.printerName ||
      !config.printerIp ||
      !config.port ||
      !config.mode
    ) {
      showNotification("Please fill in all fields", "warning");
      return false;
    }

    setSaving(true);
    try {
      if (currentConfig) {
        // Update existing config
        const res = await http.put(
          `/printerconfig/${currentConfig._id}`,
          config
        );
        if (res?.data?.success) {
          showNotification(
            "Printer configuration updated successfully",
            "success"
          );
          fetchConfig();
        }
      } else {
        // Create new config
        const res = await http.post("/printerconfig", config);
        if (res?.data?.success) {
          showNotification(
            "Printer configuration created successfully",
            "success"
          );
          fetchConfig();
        }
      }
    } catch (err) {
      console.error("Error saving printer config:", err);
      showNotification(
        err.response?.data?.message || "Failed to save printer configuration",
        "error"
      );
    } finally {
      setSaving(false);
    }

    return false;
  };

  const handleDelete = async () => {
    if (!currentConfig) return;

    if (
      !window.confirm(
        "Are you sure you want to delete this printer configuration?"
      )
    ) {
      return;
    }

    try {
      const res = await http.delete(`/printerconfig/${currentConfig._id}`);
      if (res?.data?.success) {
        showNotification(
          "Printer configuration deleted successfully",
          "success"
        );
        setCurrentConfig(null);
        resetForm(); // Reset form with USB as default
        fetchConfig();
      }
    } catch (err) {
      console.error("Error deleting printer config:", err);
      showNotification(
        err.response?.data?.message || "Failed to delete printer configuration",
        "error"
      );
    }
  };

  const handleEdit = () => {
    if (currentConfig) {
      setConfig({
        printerName: currentConfig.printerName || "",
        printerIp: currentConfig.printerIp || "",
        port: currentConfig.port || "",
        mode: currentConfig.mode || "USB",
      });
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "80vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className="printer-config-container">
      <h2 className="page-title">Printer Configuration</h2>

      <div className="printer-layout">
        {/* Form Section - Left Side */}
        <div className="form-section">
          <div className="form-header">
            <h3>{currentConfig ? "Edit Printer" : "Add Printer"}</h3>
            <div className="form-actions">
              <button
                type="submit"
                form="printer-form"
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? "Saving..." : currentConfig ? "Update" : "Create"}
              </button>
              {currentConfig && (
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={resetForm}
                  disabled={saving}
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
          <form
            id="printer-form"
            onSubmit={handleSubmit}
            className="printer-form"
            noValidate
          >
            <div className="form-group">
              <label htmlFor="printerName">Printer Name *</label>
              <input
                id="printerName"
                name="printerName"
                type="text"
                className="form-control"
                value={config.printerName}
                onChange={handleChange}
                placeholder="Enter printer name"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="printerIp">IP Address *</label>
              <input
                id="printerIp"
                name="printerIp"
                type="text"
                className="form-control"
                value={config.printerIp}
                onChange={handleChange}
                placeholder="e.g., 192.168.1.100"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="port">Port *</label>
              <input
                id="port"
                name="port"
                type="number"
                className="form-control"
                value={config.port}
                onChange={handleChange}
                placeholder="e.g., 9100"
                required
                min="1"
                max="65535"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mode">Connection Mode *</label>
              <select
                id="mode"
                name="mode"
                className="form-control"
                value={config.mode}
                onChange={handleChange}
                required
              >
                {MODE_OPTIONS.map((mode) => (
                  <option key={mode} value={mode}>
                    {mode}
                  </option>
                ))}
              </select>
            </div>
          </form>
        </div>

        {/* Record Section - Right Side */}
        <div className="record-section">
          <h3>Current Printer Configuration</h3>
          {currentConfig ? (
            <div className="config-card">
              <div className="config-header">
                <h4>{currentConfig.printerName}</h4>
                <span
                  className={`mode-badge mode-${currentConfig.mode?.toLowerCase()}`}
                >
                  {currentConfig.mode}
                </span>
              </div>
              <div className="config-body">
                <div className="config-detail-item">
                  <span className="detail-label">IP Address</span>
                  <span className="detail-value">
                    {currentConfig.printerIp}
                  </span>
                </div>
                <div className="config-detail-item">
                  <span className="detail-label">Port</span>
                  <span className="detail-value">{currentConfig.port}</span>
                </div>
                <div className="config-detail-item">
                  <span className="detail-label">Mode</span>
                  <span className="detail-value">{currentConfig.mode}</span>
                </div>
              </div>
              <div className="config-footer">
                <button
                  className="btn btn-primary"
                  onClick={handleEdit}
                  disabled={saving}
                >
                  Edit
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDelete}
                  disabled={saving}
                >
                  Delete
                </button>
              </div>
            </div>
          ) : (
            <div className="empty-state">
              <p>No printer configuration found.</p>
              <p>Create one using the form on the left.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
